import { az } from './az';
import { en } from './en';
import { ru } from './ru';
import { tr } from './tr';
import { uz } from './uz';

export const LOCALES = ['en', 'ru', 'az', 'tr', 'uz'] as const;
export type AppLocale = (typeof LOCALES)[number];

export const catalogs: Record<AppLocale, Record<string, string>> = { en, ru, az, tr, uz };

export type TVars = Record<string, string | number>;

/** Maps a Telegram `language_code` (e.g. "ru", "az", "en-US") to a supported locale. */
export function resolveLocale(code: string | null | undefined): AppLocale {
  const base = (code ?? '').toLowerCase().split(/[-_]/)[0];
  return (LOCALES as readonly string[]).includes(base ?? '') ? (base as AppLocale) : 'en';
}

const pluralRules = new Map<AppLocale, Intl.PluralRules>();
function pluralForm(locale: AppLocale, count: number): string {
  let rules = pluralRules.get(locale);
  if (!rules) pluralRules.set(locale, (rules = new Intl.PluralRules(locale)));
  return rules.select(count);
}

/**
 * Translates `key`. With a numeric `count` var, the plural form `key.<one|few|many|other>` is used
 * when the catalog defines it. Missing keys fall back to English, then to the key itself.
 */
export function t(locale: AppLocale, key: string, vars?: TVars): string {
  const catalog = catalogs[locale] ?? en;
  let template: string | undefined;
  if (vars && typeof vars.count === 'number') {
    const form = pluralForm(locale, vars.count);
    template =
      catalog[`${key}.${form}`] ?? catalog[`${key}.other`] ?? en[`${key}.${form}`] ?? en[`${key}.other`];
  }
  template ??= catalog[key] ?? en[key] ?? key;
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) => (name in vars ? String(vars[name]) : match));
}

/** Keys of a catalog with plural suffixes folded to the base key (used by the parity test). */
export function baseKeys(catalog: Record<string, string>): Set<string> {
  return new Set(Object.keys(catalog).map((k) => k.replace(/\.(zero|one|two|few|many|other)$/, '')));
}
